// ============================================================================
// Hook TanStack Query — ORGANIGRAMME (E1c) : GET /api/org + export PDF
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). La logique d'autorisation vit au
// SERVEUR (viewer renvoye par l'API — jamais recalcule au callsite, lecon GA-05).
// L'ARBRE N'EST PAS CONSTRUIT ICI (§H.1) : le serveur sert des lignes plates et la
// page reconstruit la foret via lib/org/hierarchy (buildOrgForest) — filtre equipe,
// recherche et centrage sans le moindre rechargement.
// Avatars : AUCUNE url (blob Firebase non migre + google_avatar_url externe,
// GEST-STORAGE-MIGRATION) -> initiales seulement cote carte (decision gravee 2).

import { useMutation, useQuery } from '@tanstack/react-query';
import { api, getRegisteredAccessToken } from '@gs/api-client';

export type GestOrgRole = 'admin' | 'manager' | 'director' | 'employee';
/** Role Systeme C brut (ug.UserCompanyBcRoles) — null = aucun role GEST. */
export type SystemCRole = 'UTILISATEUR' | 'GESTIONNAIRE' | 'ADMIN';

export interface OrgTeamDto {
  id: string;
  name: string;
  directorEmail: string | null;
}

export interface OrgPersonDto {
  email: string;
  fullName: string | null;
  jobTitle: string | null;
  teamId: string | null;
  managerEmail: string | null;
  committee: string | null;
  systemCRole: SystemCRole | null;
  /** Echelon d'entreprise saisi — toujours porte (null = aucun echelon). */
  orgLevel: 'gestionnaire' | null;
}

export interface OrgResponse {
  teams: OrgTeamDto[];
  people: OrgPersonDto[];
  viewer: { role: GestOrgRole; email: string };
}

const ORG_KEY = ['gest', 'org'] as const;

/** F-232 : equipes + personnes ACTIVES (M-213) de la compagnie effective. */
export function useOrg(enabled: boolean) {
  return useQuery({
    queryKey: ORG_KEY,
    queryFn: () => api.get<OrgResponse>('/api/org'),
    enabled,
    // 403 (corporate sans standing GEST, Q1) = etat stable — pas de retry.
    retry: false,
  });
}

export interface OrgPdfExportPayload {
  /** Capture PNG du canvas (data URI) produite par html-to-image. */
  image: string;
  /** Equipe filtree — le NOM du titre est resolu SERVEUR depuis ce team_id. */
  teamId: string | null;
}

// Meme convention de base d'URL qu'@gs/api-client (apiFetch) : gest est servi en
// same-origin (proxy Vite /api en dev), donc '' en pratique.
const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

/** Statut HTTP porte jusqu'a la page pour le mapping i18n (GA-21 — jamais de code brut). */
export class OrgPdfExportError extends Error {
  constructor(readonly status: number) {
    super(`Export PDF echoue (${status})`);
    this.name = 'OrgPdfExportError';
  }
}

/**
 * F-236 / M-214 : export PDF. Le serveur emballe la capture dans le gabarit brande
 * et audite (org_chart.pdf_exported). La reponse est BINAIRE : `api.*` parse du JSON
 * et ne convient pas — on fait donc un fetch authentifie explicite, avec le meme
 * Bearer que le reste du parc (getRegisteredAccessToken, @gs/api-client). Chemin
 * RELATIF uniquement (jamais une URL absolue : le Bearer GS ne sort pas de l'app).
 * L'export PNG (F-235), lui, est 100 % client — aucune mutation, aucun serveur.
 */
export function useExportOrgPdf() {
  return useMutation({
    mutationFn: async (payload: OrgPdfExportPayload): Promise<Blob> => {
      const getToken = getRegisteredAccessToken();
      if (!getToken) throw new OrgPdfExportError(401);
      const token = await getToken();
      const res = await fetch(`${BASE_URL}/api/org/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new OrgPdfExportError(res.status);
      return res.blob();
    },
  });
}
